import { useState, useRef } from "react";
import { applyTheme } from "../../constants/themes";
import { ACCENT_LOCALES, getAvailableVoices, speak } from "../../utils/words";
import { IconPicker } from "./IconPicker";
import { ThemePicker } from "./ThemePicker";
import { setUserLayoutPreference, fbSet } from "../../utils/firebase";

import maisha1 from "../../assets/avatars/maisha-1.webp";
import maisha2 from "../../assets/avatars/maisha-2.webp";
import maisha3 from "../../assets/avatars/maisha-3.webp";
import maisha4 from "../../assets/avatars/maisha-4.webp";
import aariv1 from "../../assets/avatars/aariv-1.webp";
import aariv2 from "../../assets/avatars/aariv-2.webp";
import aariv3 from "../../assets/avatars/aariv-3.webp";
import aariv4 from "../../assets/avatars/aariv-4.webp";

const AVATAR_IMAGES = {
  "maisha-1": maisha1, "maisha-2": maisha2, "maisha-3": maisha3, "maisha-4": maisha4,
  "aariv-1":  aariv1,  "aariv-2":  aariv2,  "aariv-3":  aariv3,  "aariv-4":  aariv4,
};

const MAISHA_AVATARS = ["maisha-1", "maisha-2", "maisha-3", "maisha-4"];
const AARIV_AVATARS  = ["aariv-1",  "aariv-2",  "aariv-3",  "aariv-4"];

export function ProfileSettings({ user, data, onDataChange, onClose }) {
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [newPin, setNewPin]                 = useState("");
  const [confirmPin, setConfirmPin]         = useState("");
  const [pwMsg, setPwMsg]                   = useState(null);
  const [saved, setSaved]                   = useState("");
  const fileRef = useRef();

  const userData = data.users.find(u => u.id === user.id);

  function toast() { setSaved("Saved ✓"); setTimeout(() => setSaved(""), 1500); }

  function updateUser(changes) {
    const newData = { ...data, users: data.users.map(u => u.id === user.id ? { ...u, ...changes } : u) };
    onDataChange(newData);
    toast();
  }

  function handleIconSelect(ic) { updateUser({ avatar: ic }); setShowIconPicker(false); }

  function handleThemeSelect(t) { updateUser({ theme: t }); applyTheme(t); }

  function handleBgUpload(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => updateUser({ bgImage: e.target.result });
    reader.readAsDataURL(file);
  }

  function handlePinChange() {
    if (!/^\d{4}$/.test(newPin) || newPin !== confirmPin) { setPwMsg({ ok: false, text: "PINs must be 4 digits and match." }); return; }
    updateUser({ pin: newPin });
    setNewPin(""); setConfirmPin("");
    setPwMsg({ ok: true, text: "PIN updated!" });
    setTimeout(() => setPwMsg(null), 3000);
  }

  function handleLayoutSelect(layout) {
    updateUser({ layoutPreference: layout });
    setUserLayoutPreference(user.id, layout);
  }

  function handleAvatarSelect(slug) {
    updateUser({ avatarImage: slug });
    fbSet(`users/${user.id}/avatarImage`, slug);
  }

  // Derived values
  const currentLayout = userData?.layoutPreference || "modern";
  const childName     = userData?.name || "";
  const childAvatars  = childName === "Maisha" ? MAISHA_AVATARS : childName === "Aariv" ? AARIV_AVATARS : null;
  const defaultAvatar = childName === "Maisha" ? "maisha-1" : "aariv-4";
  const currentAvatar = userData?.avatarImage || defaultAvatar;

  const SEL_BORDER = "2px solid #2563eb";

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <div className="card-title">My Profile — {userData?.name}</div>
        <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
          {saved && <span className="save-indicator">{saved}</span>}
          {onClose && <button className="wm-btn secondary" style={{ padding: ".4rem .9rem" }} onClick={onClose}>✕ Close</button>}
        </div>
      </div>

      {/* ── LAYOUT STYLE — children only ──────────────────────────────────────── */}
      {user.role === "child" && (
        <div className="profile-section">
          <div className="profile-section-title">Layout Style</div>
          <div style={{ display: "flex", gap: 10 }}>
            {[
              { id: "modern",    icon: "📋", label: "Modern",    sub: "Clean dashboard" },
              { id: "adventure", icon: "🗺️", label: "Adventure", sub: "Gamified map"    },
            ].map(opt => {
              const active = currentLayout === opt.id;
              return (
                <div key={opt.id} onClick={() => handleLayoutSelect(opt.id)}
                  style={{
                    position: "relative", flex: 1, borderRadius: 14, padding: "1rem",
                    border: active ? SEL_BORDER : "1.5px solid var(--border)",
                    background: active ? "var(--surface2)" : "var(--surface)",
                    cursor: "pointer", textAlign: "center",
                  }}>
                  {active && (
                    <span style={{ position: "absolute", top: 6, right: 8, background: "#2563eb", color: "#fff", fontSize: ".65rem", fontWeight: 800, borderRadius: 999, padding: ".15rem .45rem" }}>✓</span>
                  )}
                  <div style={{ fontSize: "1.75rem", marginBottom: ".35rem" }}>{opt.icon}</div>
                  <div style={{ fontWeight: 800, fontSize: ".88rem" }}>{opt.label}</div>
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".15rem" }}>{opt.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── YOUR AVATAR — children only ───────────────────────────────────────── */}
      {user.role === "child" && childAvatars && (
        <div className="profile-section">
          <div className="profile-section-title">Your Avatar</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {childAvatars.map(slug => {
              const active = currentAvatar === slug;
              return (
                <img key={slug} src={AVATAR_IMAGES[slug]} alt={slug}
                  onClick={() => handleAvatarSelect(slug)}
                  style={{
                    width: 64, height: 64, borderRadius: "50%", objectFit: "cover",
                    cursor: "pointer",
                    border: active ? "2px solid #f59e0b" : "2px solid var(--border)",
                    boxShadow: active ? "0 0 0 3px rgba(245,158,11,.3)" : "none",
                    transition: "border .15s, box-shadow .15s",
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── THEME / SKIN ──────────────────────────────────────────────────────── */}
      <div className="profile-section">
        <div className="profile-section-title">Theme / Skin</div>
        <ThemePicker current={userData?.theme || "ocean"} onSelect={handleThemeSelect} />
      </div>

      {/* ── PROFILE ICON ──────────────────────────────────────────────────────── */}
      <div className="profile-section">
        <div className="profile-section-title">Profile Icon</div>
        <div className="inline-row">
          <span style={{ fontSize: "2.5rem" }}>{userData?.avatar}</span>
          <button className="wm-btn secondary" onClick={() => setShowIconPicker(s => !s)}>
            {showIconPicker ? "Close" : "Change Icon"}
          </button>
        </div>
        {showIconPicker && <IconPicker current={userData?.avatar} onSelect={handleIconSelect} />}
      </div>

      {/* ── BACKGROUND IMAGE ──────────────────────────────────────────────────── */}
      <div className="profile-section">
        <div className="profile-section-title">Background Image</div>
        <div className="card-sub" style={{ marginBottom: ".75rem" }}>Upload a photo — it will appear behind all your screens</div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => handleBgUpload(e.target.files[0])} />
        {userData?.bgImage ? (
          <div style={{ display: "flex", gap: ".75rem", alignItems: "center" }}>
            <img src={userData.bgImage} alt="bg" style={{ width: 80, height: 50, objectFit: "cover", borderRadius: 8, border: "2px solid var(--border)" }} />
            <button className="wm-btn secondary" onClick={() => fileRef.current.click()}>Change</button>
            <button className="wm-btn danger" style={{ padding: ".5rem .9rem" }} onClick={() => updateUser({ bgImage: null })}>Remove</button>
          </div>
        ) : (
          <div className="file-drop" onClick={() => fileRef.current.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("over"); }}
            onDragLeave={e => e.currentTarget.classList.remove("over")}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("over"); handleBgUpload(e.dataTransfer.files[0]); }}>
            📷 Tap to choose a photo, or drag and drop here
          </div>
        )}
      </div>

      {/* ── CHANGE PIN ────────────────────────────────────────────────────────── */}
      <div className="profile-section">
        <div className="profile-section-title">🔐 Change PIN</div>
        <div className="inline-row" style={{ marginBottom: ".5rem" }}>
          <input className="wm-input" type="password" placeholder="New PIN (4 digits)" maxLength={4} value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ""))} style={{ maxWidth: 160 }} />
          <input className="wm-input" type="password" placeholder="Confirm PIN" maxLength={4} value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ""))} style={{ maxWidth: 160 }} />
        </div>
        <button className="wm-btn" onClick={handlePinChange}>Update PIN</button>
        {pwMsg && <div className={pwMsg.ok ? "auth-ok" : "auth-err"} style={{ textAlign: "left", marginTop: ".5rem" }}>{pwMsg.text}</div>}
      </div>

      {/* ── VOICE PREFERENCES — children only ────────────────────────────────── */}
      {user.role === "child" && (
        <div className="profile-section">
          <div className="profile-section-title">🔊 Spelling Test Voice</div>
          <div className="card-sub" style={{ marginBottom: ".85rem" }}>Customise how words are read aloud during spelling tests. Available voices depend on your device.</div>

          <div style={{ marginBottom: ".75rem" }}>
            <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--muted)", marginBottom: ".35rem" }}>Accent</div>
            <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
              {ACCENT_LOCALES.map(a => {
                const avail    = getAvailableVoices().some(v => v.lang.startsWith(a.code.split("-")[0]));
                const selected = (userData?.voicePrefs?.accent || "en-AU") === a.code;
                return (
                  <button key={a.code} type="button"
                    onClick={() => updateUser({ voicePrefs: { ...(userData?.voicePrefs || {}), accent: a.code } })}
                    style={{ fontSize: ".8rem", padding: ".35rem .75rem", borderRadius: 20, border: `2px solid ${selected ? "var(--accent)" : "var(--border)"}`, background: selected ? "var(--accent)" : "var(--surface2)", color: selected ? "#fff" : avail ? "var(--text)" : "var(--muted)", cursor: "pointer", fontWeight: selected ? 700 : 500, opacity: avail ? 1 : 0.5 }}>
                    {a.label}{!avail && " ✗"}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: ".75rem" }}>
            <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--muted)", marginBottom: ".35rem" }}>Voice Gender</div>
            <div style={{ display: "flex", gap: ".4rem" }}>
              {[["any","🎲 Auto"],["female","👩 Female"],["male","👨 Male"]].map(([val, label]) => {
                const selected = (userData?.voicePrefs?.gender || "any") === val;
                return (
                  <button key={val} type="button"
                    onClick={() => updateUser({ voicePrefs: { ...(userData?.voicePrefs || {}), gender: val } })}
                    style={{ fontSize: ".82rem", padding: ".35rem .85rem", borderRadius: 20, border: `2px solid ${selected ? "var(--accent)" : "var(--border)"}`, background: selected ? "var(--accent)" : "var(--surface2)", color: selected ? "#fff" : "var(--text)", cursor: "pointer", fontWeight: selected ? 700 : 500 }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: ".85rem", display: "flex", alignItems: "center", gap: ".75rem", background: "var(--surface2)", borderRadius: 12, padding: ".75rem 1rem" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: ".85rem" }}>💬 Sentence Mode</div>
              <div style={{ fontSize: ".75rem", color: "var(--muted)", marginTop: ".15rem" }}>Show a "Hear it in a sentence" button during spelling tests</div>
            </div>
            <button type="button"
              onClick={() => updateUser({ voicePrefs: { ...(userData?.voicePrefs || {}), sentenceMode: !(userData?.voicePrefs?.sentenceMode ?? true) } })}
              style={{ fontSize: ".82rem", padding: ".4rem .9rem", borderRadius: 20, border: `2px solid ${(userData?.voicePrefs?.sentenceMode ?? true) ? "var(--accent)" : "var(--border)"}`, background: (userData?.voicePrefs?.sentenceMode ?? true) ? "var(--accent)" : "var(--surface2)", color: (userData?.voicePrefs?.sentenceMode ?? true) ? "#fff" : "var(--text)", cursor: "pointer", fontWeight: 700, minWidth: 56 }}>
              {(userData?.voicePrefs?.sentenceMode ?? true) ? "On" : "Off"}
            </button>
          </div>

          <button className="wm-btn secondary" onClick={() => speak("The quick brown fox jumps over the lazy dog", userData?.voicePrefs)}>
            🔊 Test Voice
          </button>
          <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".4rem" }}>
            Selected: {ACCENT_LOCALES.find(a => a.code === (userData?.voicePrefs?.accent || "en-AU"))?.label || "Australian"} · {userData?.voicePrefs?.gender === "male" ? "Male" : userData?.voicePrefs?.gender === "female" ? "Female" : "Auto"}
          </div>
        </div>
      )}
    </div>
  );
}
